import React, { JSX, MouseEvent, useEffect, useRef, useState } from 'react';
import { Button, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import {
  ApiBaseData,
  ApiGetBestPricePackagesResponse,
  ApiGetFlightTripPackageRequestFromJSON,
  ApiItemType,
  ApiRentalCarOffice
} from '@ibe/api';
import fallback from '../../../../_Translations/generated/bus-stops-checkbox-async.en.t.json';
import Keys from '../../../../_Translations/generated/bus-stops-checkbox-async.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import Spinner from '../../UI/LoadingOverlay/Spinner';
import useApi from '../../../Hooks/useApi';
import { useClickAway } from 'react-use';
import { LoggerFactory } from '@ibe/services';
import DistanceUtil from '../../../../Utils/DistanceUtil';
import RentalCarOfficeWithDistanceWrapper from '../../../../Models/RentalCarOfficeWithDistanceWrapper';
import BestPriceAvailabilitySearchStore from '../../../SubModules/BestPriceAvailabilitySearch/BestPriceAvailabilitySearchStore';
import {
  busStationsAnchor,
  PackageCode
} from '../../../SubModules/BestPriceAvailabilitySearch/PackageDropdownFilterRenderer';
import ScrollerService from '../../../Services/ScrollerService';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import PostCodeSelection from './PostCodeSelection';

const logger = LoggerFactory.get('BusStopsCheckboxAsync.tsx');

export interface Props {
  value: ApiRentalCarOffice[] | undefined;
  placeholder?: string;
  onLoadStations: (value: RentalCarOfficeWithDistanceWrapper[]) => void;
  name?: string;
  withSelectAll?: boolean;
  withRemoveAll?: boolean;
  disabled?: boolean;
  isOpen: boolean;
  ownArrivalsAndDepartures?: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<RentalCarOfficeWithDistanceWrapper[]>>;
  setSelectedPostCode: React.Dispatch<React.SetStateAction<ApiBaseData | undefined>>;
  selectedPostCode: ApiBaseData | undefined;
  selectedOptions: RentalCarOfficeWithDistanceWrapper[];
  store: BestPriceAvailabilitySearchStore;
  packageCodes?: PackageCode[];
}

const BusStopsCheckboxAsync = (props: Props): JSX.Element => {
  const {
    value = [],
    placeholder,
    name,
    onLoadStations,
    withSelectAll,
    withRemoveAll,
    disabled,
    selectedPostCode,
    setSelectedPostCode,
    isOpen,
    setIsOpen,
    store,
    packageCodes,
    setSelectedOptions,
    selectedOptions,
    ownArrivalsAndDepartures
  } = props;
  const { t } = useTranslation('bus-stops-checkbox-async', fallback, Keys);
  const api = useApi();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [optionsLoaded, setOptionsLoaded] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const [sortedRentalCarOffice, setSortedRentalCarOffice] = useState<
    RentalCarOfficeWithDistanceWrapper[]
  >([]);

  useEffect(() => {
    if (selectedPostCode) {
      setOptionsLoaded(false);
      sessionStorage.setItem('selectedPostCode', JSON.stringify(selectedPostCode));
    }
  }, [selectedPostCode]);

  const fetchInitialPackages = async (): Promise<ApiGetBestPricePackagesResponse | undefined> => {
    try {
      return await api.searchFlightTripPackages(
        ApiGetFlightTripPackageRequestFromJSON({
          busStations: [selectedPostCode?.code],
          startDate: store.searchParams.startDate,
          endDate: store.searchParams.endDate,
          roomContainer: [
            {
              adults:
                store.searchParams?.occupancy?.find(item => item?.adults !== undefined)?.adults ||
                2,
              children: 0,
              infants: 0,
              childrenAges: [],
              infantsAges: []
            }
          ],
          origins: store.searchParams?.origins,
          packageCodes: (packageCodes || []).map(code => ({
            packageCode: code.code,
            smallGroupOnly: code.groupSize
          })),
          subType: ApiItemType.PACKAGE,
          promotionCode: undefined
        })
      );
    } catch (err) {
      logger.log(err);
    }
  };

  const loadOptions = async (selected: ApiBaseData): Promise<void> => {
    setIsLoading(true);

    const packageSearchResponse = await fetchInitialPackages();

    if (packageSearchResponse && packageSearchResponse.packages.length > 0) {
      const request = {
        serviceClass: 'CAR',
        perimeterLatitude: selected.latitude,
        perimeterLongitude: selected.longitude,
        perimeterRadiusInKm: 100
      };

      try {
        const rentalCarOfficesResponse = await api.getRentalCarOffices(request);

        // Filter stations that contain any packages
        const officeCodesSet = new Set(rentalCarOfficesResponse.offices.map(office => office.code));
        const possibleStations = Array.from(
          new Set(
            packageSearchResponse.packages.flatMap(
              p =>
                p.stationCodes
                  ?.filter(stationCode => officeCodesSet.has(stationCode))
                  .map(stationCode =>
                    rentalCarOfficesResponse.offices.find(office => office.code === stationCode)
                  )
                  .filter(office => office !== undefined) || []
            )
          )
        );

        // Sort stations by haversine distance
        const sorted = DistanceUtil.sortByHaversineDistance(
          possibleStations as RentalCarOfficeWithDistanceWrapper[],
          selected
        );

        onLoadStations(sorted);
        setSortedRentalCarOffice(sorted.slice(0, 9));
        setOptionsLoaded(true);
      } catch (err) {
        logger.log(err);
      }
    }
  };

  useEffect(() => {
    if (selectedPostCode && !optionsLoaded) {
      loadOptions(selectedPostCode).then(() => {
        setIsOpen(true);
        ScrollerService.scrollTo(busStationsAnchor, { offset: 0 });
        setIsLoading(false);
      });
    }
  }, [selectedPostCode, optionsLoaded]);

  useClickAway(popupRef, (): void => {
    setIsOpen(!isOpen);
  });

  const onOptionClick = (option: RentalCarOfficeWithDistanceWrapper): void => {
    const foundOption = selectedOptions.find(
      selectedOption => selectedOption?.code === option.code
    );
    let newSelectedOptions = [...selectedOptions];
    if (foundOption) {
      newSelectedOptions = [
        ...newSelectedOptions.filter(selectedOption => selectedOption?.code !== option.code)
      ];
    } else {
      newSelectedOptions = [...newSelectedOptions, { ...option }];
    }
    setSelectedOptions(newSelectedOptions);
  };

  const handleClick = async (e: MouseEvent<HTMLInputElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const applySelection = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    sessionStorage.setItem('selectedStations', JSON.stringify(selectedOptions));
    setIsOpen(false);
  };

  let busStationsInfo = '';
  if (!ownArrivalsAndDepartures && store.postalCode) {
    busStationsInfo = store.postalCode.concat('            ');
    if (store.isBusStationsAvailable) {
      busStationsInfo = busStationsInfo
        .concat(selectedOptions ? selectedOptions.length.toString(10) : '0')
        .concat(' ')
        .concat(t(Keys.stopsSelected));
    } else {
      busStationsInfo = busStationsInfo.concat('0 ').concat(t(Keys.stopsAvailable));
    }
  }

  const fieldValue = value.map(option => option?.description || '').join(', ');

  if (store.postalCode) {
    return (
      <label className="custom-fieldset-label">
        <div
          className={`geounit-checkbox${
            isOpen && sortedRentalCarOffice.length > 0 ? ' geounit-checkbox--open' : ''
          }${disabled ? ' geounit-checkbox--disabled' : ''}`}
        >
          <div>
            <span className="geounit-checkbox__angle__wrapper">
              <FontAwesomeIcon className="geounit-checkbox__angle" icon={faAngleDown} />
            </span>
            <Input
              className={!!fieldValue ? 'input--filled' : ''}
              innerRef={inputRef}
              name={name || ''}
              placeholder={placeholder || ''}
              onClick={handleClick}
              value={busStationsInfo}
              readOnly
              disabled={disabled}
            />
          </div>
          {isLoading && (
            <div className="geounit-checkbox__loading">
              <Spinner />
            </div>
          )}
          {isOpen && sortedRentalCarOffice.length > 0 ? (
            <div className="geounit-checkbox__popover" ref={popupRef}>
              <div>
                <div className="geounit-checkbox__container">
                  <div className="geounit-checkbox__container__inner">
                    {sortedRentalCarOffice && withSelectAll && (
                      <CheckBox
                        id={'select-all'}
                        name={
                          selectedOptions.length === sortedRentalCarOffice.length
                            ? t(Keys.removeAll)
                            : t(Keys.selectAll)
                        }
                        onChange={(): void =>
                          setSelectedOptions(
                            selectedOptions.length === sortedRentalCarOffice.length
                              ? []
                              : sortedRentalCarOffice
                          )
                        }
                        checked={selectedOptions.length === sortedRentalCarOffice.length}
                      />
                    )}

                    {sortedRentalCarOffice &&
                      sortedRentalCarOffice.map((option, index) => {
                        return (
                          <CheckBox
                            key={index}
                            id={index.toString()}
                            name={option.description + ' (' + Math.round(option.distance) + ' km)'}
                            onChange={(): void => onOptionClick(option)}
                            checked={
                              !!selectedOptions.find(
                                selectedOption => selectedOption?.code === option.code
                              )
                            }
                          />
                        );
                      })}
                  </div>
                  <div className="geounit-checkbox__button-container d-flex justify-content-center mt-4 mb-2">
                    {withRemoveAll && selectedOptions.length ? (
                      <button
                        type="button"
                        className="btn geounit-checkbox__btn-remove"
                        onClick={() => setSelectedOptions([])}
                      >
                        {t(Keys.removeAllChoices)}
                      </button>
                    ) : null}
                    <Button color="secondary" onClick={applySelection}>
                      {t(Keys.apply)}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
        {!ownArrivalsAndDepartures && (
          <span className="remove-icon">
            <FontAwesomeIcon
              icon={faTimesCircle}
              onClick={(): void => {
                store.setPostalCode(undefined);
                sessionStorage.removeItem('selectedPostCode');
                sessionStorage.removeItem('selectedStations');
                setSelectedOptions([]);
              }}
            />
            <span className="separator"></span>
          </span>
        )}
        <span className="placeholder">{t(Keys.plz)}</span>
      </label>
    );
  } else {
    return (
      <PostCodeSelection
        setSelectedPostCode={setSelectedPostCode}
        setIsOpen={setIsOpen}
        store={store}
        ownArrivalsAndDepartures={ownArrivalsAndDepartures}
      />
    );
  }
};

const CheckBox = ({
  id,
  checked,
  onChange,
  name
}: {
  id: string;
  checked?: boolean;
  onChange: () => void;
  name: string;
}) => {
  return (
    <div className="geounit-checkbox__field bus-stops">
      <label>
        <Input id={id} type="checkbox" checked={checked} onChange={onChange} />
        <span>{name}</span>
      </label>
    </div>
  );
};

export default BusStopsCheckboxAsync;
